<script lang="ts" setup>
import { computed, onMounted, ref, Ref } from 'vue';
import Chip from 'primevue/chip';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useArchiveStore, useSchoolAdminStore } from '@/store';
import { SchoolAdminModal, SchoolAdminTable } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const schoolAdminStore = useSchoolAdminStore();
const archiveStore = useArchiveStore();
const schoolAdmins: Ref<SchoolAdminTable[]> = ref([]);
const schoolAdminCount: Ref<number> = computed(() => (schoolAdmins.value ? schoolAdmins.value.length : 0));
const schoolAdminModalOpen: Ref<boolean> = ref(false);
const archivedVisible = ref(false);
const schoolAdminItems: Ref<SchoolAdminModal[]> = ref([]);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_SCHOOL_ADMIN_INDEX])) getSchoolAdmins(false);
});

const getSchoolAdmins: (showArchived: boolean) => Promise<void> = async (showArchived: boolean) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  await schoolAdminStore
    .getSchoolAdminsForTable(filterArchived)
    .then((result) => (schoolAdmins.value = result))
    .catch((e) => {
      schoolAdmins.value = [];
      throw e;
    });
};

const createMenuModel = (item: SchoolAdminTable) => {
  const menuItems = [
    {
      label: 'Remove School Admin',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_SCHOOL_ADMINS]),
      command: () => {
        detachSchoolAdmin([item.id], item.name);
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachSchoolAdmins: (ids: number[]) => Promise<void> = async (ids) => {
  await schoolAdminStore.attachSchoolAdmins(ids);
  getSchoolAdmins(false);
  schoolAdminStore.doToast('School Admin(s) has been added', 'Added');
};

const detachSchoolAdmin: (ids: number[], schoolAdminName: string) => Promise<void> = async (ids, schoolAdminName) => {
  await schoolAdminStore.detachSchoolAdmins(ids);
  getSchoolAdmins(false);
  schoolAdminStore.doToast(`${schoolAdminName} has been removed from organization`, 'Removed');
};

const openAddSchoolAdminModal = async () => {
  schoolAdminModalOpen.value = true;
  schoolAdminItems.value = await schoolAdminStore.getSchoolAdminsAvailableToAdd();
};

const onCancelAdd = () => (schoolAdminModalOpen.value = false);

const onSaveAdd = (schoolAdmins: SchoolAdminModal[]) => {
  let schoolAdminIds = schoolAdmins.map((schoolAdmins) => {
    return schoolAdmins.value;
  });
  attachSchoolAdmins(schoolAdminIds);
  schoolAdminModalOpen.value = false;
};
const showArchived = () => {
  archivedVisible.value = true;
  schoolAdminStore.showArchived = true;
  getSchoolAdmins(true);
};
const hideArchived = () => {
  archivedVisible.value = false;
  schoolAdminStore.showArchived = false;
  getSchoolAdmins(false);
};
</script>

<template>
  <Card>
    <template #header>
      <h2>School Admins</h2>
      <Chip>{{ schoolAdminCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATION_SCHOOL_SHOW_ARCHIVED])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_ATTACH_SCHOOL_ADMINS])"
          type="text"
          size="small"
          text="Add School Admin"
          icon-left="add"
          @click="openAddSchoolAdminModal"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="schoolAdmins"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :row-class="({ archived }) => (archived ? 'grey-row' : null)"
        :loading="schoolAdminStore.loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-school-admin-table"
        state-storage="session"
        :class="{ 'table-loading': schoolAdminStore.loading }"
      >
        <template v-if="schoolAdminCount">
          <Column field="name" header="School Admin Name" header-style="color: #3B4242" sortable>
            <template #body="{ data }">
              <Icon
                v-if="data.archived"
                v-tooltip.top="{
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                }"
                name="info"
                class="mr-1 text-color-gray-dark text-base cursor-pointer"
              />
              <RouterLink
                v-if="can([PERMISSIONS.PEOPLE_INDEX])"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.name"
              />
              <span v-else class="font-semibold" v-text="data.name" />
            </template>
          </Column>
          <Column
            body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
            </template>
          </Column>
        </template>
        <template #empty>School Admins not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="schoolAdminModalOpen && can([PERMISSIONS.ORGANIZATION_ATTACH_SCHOOL_ADMINS])"
        title="Add school admins"
        :items="schoolAdminItems"
        :searchable="true"
        button-text="Add school admins"
        placeholder="Search school admins"
        person-type="School admin"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
</style>
